import React from "react"
import {
  type EntityParty,
  type Flow,
  type PartyHover,
} from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgBuilding02 } from "@digits-shared/components/SVGIcons/line/Building02.svg"
import { usePopoverMutexContext } from "@digits-shared/components/UI/Elements/PopUp/PopOverMutexContext"
import { DigitsLinkButton } from "@digits-shared/DesignSystem/LinkButton"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { AgingChartComponent } from "src/frontend/components/Shared/Layout/Components/Charts/AgingChartComponent"
import useRowSelectionContext from "src/frontend/components/Shared/Layout/Components/Statements/RowSelectionContext"
import { type RowDetails } from "src/frontend/components/Shared/Layout/Components/Statements/toDetailsData"
import { ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import { DetailsPopUp } from "src/frontend/components/Shared/Layout/PopOver/DetailsPopOver/DetailsPopUp"
import { type PopOverPositioning } from "src/frontend/components/Shared/Layout/PopOver/DetailsPopOver/usePopOverPositioning"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"

const AgingSummaryChartHoverTitle = styled.div`
  display: flex;
  height: 48px;
  padding: 0 16px;
  align-items: center;
  align-self: stretch;
  justify-content: space-between;
  border-radius: 8px 8px 0 0px;
  background: ${colors.secondary05};
  font-weight: ${fonts.weight.heavy};
`

const AgingSummaryChartHoverTitleName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  svg {
    width: 16px;
    height: 16px;
    aspect-ratio: 1/1;
    flex-shrink: 0;
    ${svgPathStyles(colors.secondary, 1.5)}
  }
`

const ChartContainer = styled.div`
  padding: 16px;
`

export interface PopOverProps {
  flow: Flow
  rowId: string
  details?: RowDetails
  onMouseEnter?: React.MouseEventHandler<HTMLElement>
}

export const AgingSummaryPopOver: React.FC<PopOverProps & PopOverPositioning> = ({
  flow,
  rowId,
  details,
  positionRef,
  measureRef,
  measureRect,
  onMouseEnter,
}) => {
  const pathGenerator = useFrontendPathGenerator()
  const { setPinnedCell } = useRowSelectionContext()
  const { clearActivePopOver } = usePopoverMutexContext()

  const onClickViewDetails = React.useCallback(() => {
    setPinnedCell(undefined)
    clearActivePopOver()
  }, [setPinnedCell, clearActivePopOver])

  if (!details?.periodDetails?.hover) {
    return null
  }
  const partyHoverEntity: EntityParty | undefined = (details.periodDetails.hover as PartyHover)
    .entity

  return (
    <DetailsPopUp
      ref={measureRef}
      rowId={rowId}
      details={details as RowDetails}
      measureRect={measureRect}
      positionRef={positionRef}
      width={420}
      maxHeight="auto"
    >
      <div onMouseEnter={onMouseEnter}>
        {partyHoverEntity.designation && (
          <AgingSummaryChartHoverTitle>
            <AgingSummaryChartHoverTitleName>
              <SvgBuilding02 />
              {partyHoverEntity.name}
            </AgingSummaryChartHoverTitleName>
            <DigitsLinkButton
              $variant="secondary-dark"
              size="small"
              to={pathGenerator(routes.partyDetails, {
                partyId: partyHoverEntity.id,
                partyRole: "vendor",
              })}
              onClick={onClickViewDetails}
            >
              View Details
            </DigitsLinkButton>
          </AgingSummaryChartHoverTitle>
        )}
        <ChartContainer>
          <AgingChartComponent
            dimensionalSummary={details.periodDetails.hover.history}
            componentSize={ComponentSize.Medium}
            height={150}
            skipAnimations={true}
            loading={false}
            flow={flow}
          />
        </ChartContainer>
      </div>
    </DetailsPopUp>
  )
}
